import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n/I18nProvider'
import { Alert, Badge, Button, Card, CheckboxField, FormActions, Grid, PageHeader, Stack, TextField, useToast } from '../components/ui'
import { billingApi } from './billingApi'
import type { SellerProfile } from './billingApi'

type Saisie = Record<
  'legalName' | 'taxId' | 'address' | 'city' | 'postalCode' | 'phone' | 'email' | 'bankName' | 'bankAccount' | 'footer' | 'vatRate' | 'stampDuty' | 'paymentTermsDays',
  string
>

const VIDE: Saisie = {
  legalName: '', taxId: '', address: '', city: '', postalCode: '', phone: '', email: '', bankName: '',
  bankAccount: '', footer: '', vatRate: '', stampDuty: '', paymentTermsDays: '',
}

function versSaisie(p: SellerProfile): Saisie {
  return {
    legalName: p.legalName, taxId: p.taxId ?? '', address: p.address ?? '', city: p.city ?? '',
    postalCode: p.postalCode ?? '', phone: p.phone ?? '', email: p.email ?? '', bankName: p.bankName ?? '',
    bankAccount: p.bankAccount ?? '', footer: p.footer ?? '', vatRate: String(p.vatRate), stampDuty: String(p.stampDuty),
    paymentTermsDays: String(p.paymentTermsDays),
  }
}

const vide = (s: string) => (s.trim() === '' ? null : s.trim())
const nombre = (s: string) => Number(s.replace(',', '.'))

/**
 * L'identité de la plateforme sur ses factures (saas.seller_profile).
 *
 * LES CHAMPS SE LISENT DE GAUCHE À DROITE, même en arabe : la facture s'imprime en français,
 * et une adresse latine saisie dans un champ de droite à gauche voit son numéro de rue passer
 * en fin de ligne.
 *
 * CE QUI EST ENREGISTRÉ ICI NE RÉÉCRIT AUCUNE FACTURE : le vendeur est recopié dans chaque
 * facture à son émission. Changer d'adresse demain vaut pour les factures de demain.
 */
export function SellerProfilePage() {
  const { t, describeError } = useI18n()
  const { session } = useAuth()
  const { notify } = useToast()

  const token = session?.token ?? ''

  const [saisie, setSaisie] = useState<Saisie>(VIDE)
  const [auto, setAuto] = useState(true)
  const [complet, setComplet] = useState<boolean | null>(null)
  const [envoi, setEnvoi] = useState(false)

  useEffect(() => {
    if (token === '') return
    const controle = new AbortController()
    billingApi
      .getSeller(token, controle.signal)
      .then((p) => {
        setSaisie(versSaisie(p))
        setAuto(p.autoInvoice)
        setComplet(p.isComplete)
      })
      .catch((cause: unknown) => {
        if (!controle.signal.aborted) notify(describeError(cause), 'erreur')
      })
    return () => controle.abort()
  }, [token, notify, describeError])

  const champ = (cle: keyof Saisie) => (valeur: string) => setSaisie((s) => ({ ...s, [cle]: valeur }))

  const enregistrerProfil = async () => {
    setEnvoi(true)
    try {
      const p = await billingApi.setSeller(token, {
        legalName: saisie.legalName.trim(),
        taxId: vide(saisie.taxId),
        address: vide(saisie.address),
        city: vide(saisie.city),
        postalCode: vide(saisie.postalCode),
        phone: vide(saisie.phone),
        email: vide(saisie.email),
        bankName: vide(saisie.bankName),
        bankAccount: vide(saisie.bankAccount),
        footer: vide(saisie.footer),
        vatRate: nombre(saisie.vatRate),
        stampDuty: nombre(saisie.stampDuty),
        paymentTermsDays: nombre(saisie.paymentTermsDays),
        autoInvoice: auto,
      })
      setSaisie(versSaisie(p))
      setComplet(p.isComplete)
      notify(t('billing.seller.saved'), 'succes')
    } catch (cause: unknown) {
      notify(describeError(cause), 'erreur')
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <Stack>
      <PageHeader title={t('billing.seller.title')} description={t('billing.seller.description')} />
      <Link to="/platform/invoices">← {t('billing.invoices.title')}</Link>

      {complet === false && <Alert tone="avertissement">{t('billing.seller.incomplete')}</Alert>}
      {complet === true && (
        <div>
          <Badge tone="succes">{t('billing.seller.ready')}</Badge>
        </div>
      )}

      <Card title={t('billing.seller.identity')}>
        <Grid min="16rem">
          <TextField label={t('billing.seller.legalName')} value={saisie.legalName} onChange={champ('legalName')} ltr required />
          <TextField label={t('billing.seller.taxId')} value={saisie.taxId} onChange={champ('taxId')} ltr required hint={t('billing.seller.taxIdHint')} />
          <TextField label={t('billing.seller.address')} value={saisie.address} onChange={champ('address')} ltr required />
          <TextField label={t('billing.seller.city')} value={saisie.city} onChange={champ('city')} ltr required />
          <TextField label={t('billing.seller.postalCode')} value={saisie.postalCode} onChange={champ('postalCode')} ltr />
          <TextField label={t('billing.seller.phone')} value={saisie.phone} onChange={champ('phone')} ltr />
          <TextField label={t('billing.seller.email')} type="email" value={saisie.email} onChange={champ('email')} ltr />
        </Grid>
      </Card>

      <Card title={t('billing.seller.terms')}>
        <Stack gap={3}>
          <Grid min="12rem">
            <TextField label={t('billing.seller.vatRate')} value={saisie.vatRate} onChange={champ('vatRate')} ltr />
            <TextField label={t('billing.seller.stampDuty')} value={saisie.stampDuty} onChange={champ('stampDuty')} ltr />
            <TextField label={t('billing.seller.paymentTermsDays')} value={saisie.paymentTermsDays} onChange={champ('paymentTermsDays')} ltr />
            <TextField label={t('billing.seller.bankName')} value={saisie.bankName} onChange={champ('bankName')} ltr />
            <TextField label={t('billing.seller.bankAccount')} value={saisie.bankAccount} onChange={champ('bankAccount')} ltr />
          </Grid>
          <TextField label={t('billing.seller.footer')} value={saisie.footer} onChange={champ('footer')} ltr hint={t('billing.seller.footerHint')} />
          <CheckboxField label={t('billing.seller.autoInvoice')} checked={auto} onChange={setAuto} hint={t('billing.seller.autoInvoiceHint')} />
          <FormActions>
            <Button onClick={enregistrerProfil} pending={envoi} disabled={saisie.legalName.trim() === ''}>
              {t('common.save')}
            </Button>
          </FormActions>
        </Stack>
      </Card>
    </Stack>
  )
}
